import posthog from "posthog-js";

type ModuleEvents = {
  customers: [
    "tab_changed",
    "card_token_copied",
    "profile_viewed",
    "list_viewed",
    "create_modal_opened",
    "edit_opened",
    "delete_initiated",
    "create_submitted",
    "create_failed",
    "detail_viewed",
    "action_subscription_created",
    "action_payment_link_created",
    "action_invoice_created",
  ];
  insights: [
    "set_account",
    "set_duration",
    "view_details",
    "tab_view",
    "provider_details",
    "method_details",
    "revenue_details",
    "transaction_details",
  ];
  routing: [
    "create_flow",
    "add_action",
    "edit_action",
    "add_trigger",
    "remove_trigger",
    "order_triggers_usage",
    "triggers_details", // collect all trigger details on publish
    "remove_action",
    "branches_details",
    "publish",
    "publish_error",
    "version_change",
    "version_rename",
    "version_duplicate",
    "paths_details",
    "trigger_usage",
    "webhook_usage",
    "provider_usage",
  ];
  user: ["logged_in", "logged_out", "forgot_password", "member_invited"];
  home: ["test_mode_toggled", "dashboard_viewed"];
  intents: ["list_viewed", "detail_viewed"];
  transactions: ["list_viewed", "detail_viewed"];
  risk: ["list_created", "rule_created"];
  flows: [
    "list_viewed",
    "builder_viewed",
    "checkout_payment_method_toggled",
    "routing_rule_applied",
    "deleted",
    "saved",
    "published",
  ];
};
type AnalyticsModuleEvents = {
  [k in keyof ModuleEvents]: `${k}_${ModuleEvents[k][number]}`;
}[keyof ModuleEvents];

type ModuleEventsWithVariants = {
  transactions: ["capture", "refund", "void", "export" | "pull"];
};

type AnalyticsModuleEventsWithVariants = {
  [k in keyof ModuleEventsWithVariants]:
    | `${k}_${ModuleEventsWithVariants[k][number]}`
    | `${k}_${ModuleEventsWithVariants[k][number]}_cancel`
    | `${k}_${ModuleEventsWithVariants[k][number]}_confirm`
    | `${k}_${ModuleEventsWithVariants[k][number]}_success`
    | `${k}_${ModuleEventsWithVariants[k][number]}_error`;
}[keyof ModuleEventsWithVariants];

export type Event =
  | AnalyticsModuleEvents
  | AnalyticsModuleEventsWithVariants
  | "page_view";

const isProduction = process.env.NEXT_PUBLIC_ENV === "production";

class AnalyticsManager {
  track(eventName: Event, properties?: Record<string, unknown>) {
    if (!isProduction) return;
    posthog.capture(eventName, properties, { transport: "sendBeacon" });
  }

  identify(properties: {
    email: string;
    name: string;
    organization_name: string;
    persona: string;
  }) {
    if (!isProduction) return;
    posthog.identify(properties.email, properties);
  }

  reset() {
    if (!isProduction) return;
    posthog.reset();
  }

  /** Registers super properties that are included with all events. */
  register(properties: Record<string, unknown>) {
    if (!isProduction) return;
    posthog.register(properties);
  }
}

export const analytics = new AnalyticsManager();
